import { useMemo } from "react"
import _ from "lodash"

import {
  getOccupantDetail,
  IMultiLanguageInfo,
  IOccupantDetail,
} from "utils/feature"

import { useGetOccupant } from "providers/venue/modules/data"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useRecommendedPromotion } from "hooks/sponsored-content/recommended"
import { PromotionData } from "providers/venue/types"
import { useGetList } from "venue-react"

export interface IUseOccupantPromotionResult extends IOccupantDetail {
  occupantName: IMultiLanguageInfo | string
  promotions: PromotionData[]
  mobileUrl: string
}

export type UseOccupantPromotion = (
  occupantId: string
) => IUseOccupantPromotionResult

export const useOccupantPromotion: UseOccupantPromotion = (occupantId) => {
  const { mobileUrl } = useMobileUrl({ path: "/maps/place/" })
  const { data: occupant } = useGetOccupant(occupantId)
  const { data: promotions } = useGetList("promotion", { populate: true })

  const { recommendedPromotions } = useRecommendedPromotion()

  const occupantDetail = useMemo(() => {
    return (occupant ? getOccupantDetail(occupant) : {}) as IOccupantDetail
  }, [occupant])

  const occupantPromotions = useMemo(
    () =>
      promotions?.filter(
        (promotion) => promotion.properties.feature?.id === occupantId
      ) ?? [],
    [promotions, occupantId]
  )

  const mapPromotionIdWithIsAd = useMemo(
    () =>
      recommendedPromotions.data.reduce(
        (acc, promotion) => ({
          ...acc,
          [promotion.id]: true,
        }),
        {}
      ),
    [recommendedPromotions.data]
  )

  const transformPromotionsWithIsAd = useMemo(
    () =>
      occupantPromotions.map((promotion) => ({
        ...promotion,
        properties: {
          ...promotion.properties,
          isAd: !!mapPromotionIdWithIsAd[promotion.id],
        },
      })),
    [mapPromotionIdWithIsAd, occupantPromotions]
  )

  const sortedPromotions = useMemo(
    () =>
      _.orderBy(
        transformPromotionsWithIsAd,
        [
          "properties.is_featured",
          "properties.start_date",
          "properties.end_date",
        ],
        ["desc", "asc", "asc"]
      ),
    [transformPromotionsWithIsAd]
  )

  return {
    ...occupantDetail,
    mobileUrl,
    occupantName: occupantDetail.name,
    promotions: sortedPromotions,
  }
}
